import React, { useState, useEffect, useMemo, useRef } from 'react';
import { SkillConfig } from '../../types/onboarding';
import { Image } from '../../../ui/Image';
import { useCreateSkill, useSkill } from '../../apis/skill-registry';
import { Button } from '../../components';
import {
  useGetOutboundIntegrations,
  useListJiraProjects,
} from '../../apis/outbound-integrations';

import { Select } from '@plerionhq/design-system';

import { useTranslation, Trans } from 'react-i18next';

interface JiraSkillSetupProps {
  skill: SkillConfig;
  onComplete: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const JiraSkillSetup: React.FC<JiraSkillSetupProps> = ({
  skill,
  onComplete,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation('pleri/onboarding');
  const [selectedIntegrationId, setSelectedIntegrationId] =
    useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [issueTypeId, setIssueTypeId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: enableSkill, isPending: isEnabling } = useCreateSkill();
  const {
    data: integrations,
    isLoading: isLoadingIntegrations,
    refetch: refetchIntegrations,
  } = useGetOutboundIntegrations();

  const handleIntegrationUpdate = (
    _selectedIndex?: number,
    selected?: { name: string; value: string },
  ): void => {
    if (!selected || typeof selected.value !== 'string') {
      setSelectedIntegrationId('');
      return;
    }
    setSelectedIntegrationId(selected.value);
  };

  const handleProjectUpdate = (
    _selectedIndex?: number,
    selected?: { name: string; value: string },
  ): void => {
    if (!selected || typeof selected.value !== 'string') {
      setProjectId('');
      return;
    }
    setProjectId(selected.value);
  };

  /**
   * The project the admin last cleared the issue type for.
   *
   * An empty issue type means two different things: the admin removed the
   * default, or the project changed and the old type no longer applies. Only the
   * first should survive, so the intent is recorded rather than inferred from the
   * field being empty.
   */
  const clearedByAdminForProject = useRef<string | null>(null);

  const handleIssueTypeUpdate = (
    _selectedIndex?: number,
    selected?: { name: string; value: string },
  ): void => {
    if (!selected || typeof selected.value !== 'string') {
      clearedByAdminForProject.current = projectId;
      setIssueTypeId('');
      return;
    }
    clearedByAdminForProject.current = null;
    setIssueTypeId(selected.value);
  };

  // Fetch existing skill configuration if skill is enabled
  const { data: existingSkill } = useSkill(skill.id, skill.enabled);

  // Get Jira projects for the selected integration
  const { data: jiraProjects = [], isLoading: isLoadingJiraProjects } =
    useListJiraProjects(selectedIntegrationId, !!selectedIntegrationId);

  const jiraOptions = useMemo(
    () =>
      integrations
        ?.filter((integration) => integration.type === 'Jira')
        .map((integration) => ({
          name: integration.name,
          value: integration.integrationId,
        })) ?? [],
    [integrations],
  );

  // Format projects for the Select component
  const projectOptions = useMemo(() => {
    return (
      jiraProjects?.map((project) => ({
        name: `${project.key} - ${project.name}`,
        value: project.id,
      })) ?? []
    );
  }, [jiraProjects]);

  // Get selected project to find its issue types
  const selectedProject = jiraProjects?.find(
    (project) => project.id === projectId,
  );

  // Format issue types for the Select component
  const issueOptions = useMemo(() => {
    if (!selectedProject) {
      return [];
    }
    return selectedProject.issueTypes.map((issueType) => ({
      name: issueType.name,
      value: issueType.id,
    }));
  }, [selectedProject]);

  // Auto-select if there's only one integration
  useEffect(() => {
    if (jiraOptions.length === 1 && !selectedIntegrationId) {
      setSelectedIntegrationId(jiraOptions[0].value);
    }
  }, [jiraOptions, selectedIntegrationId]);

  // Reset project and issue type when integration changes
  useEffect(() => {
    if (selectedIntegrationId !== existingSkill?.config?.integrationId) {
      setProjectId('');
      setIssueTypeId('');
    }
  }, [selectedIntegrationId, existingSkill?.config?.integrationId]);

  // Reset issue type when project changes
  useEffect(() => {
    if (projectId !== existingSkill?.config?.projectId) {
      setIssueTypeId('');
    }
  }, [projectId, existingSkill?.config?.projectId]);

  // Initialize state with existing configuration
  useEffect(() => {
    if (existingSkill?.config) {
      const config = existingSkill.config;
      if (typeof config.integrationId === 'string') {
        setSelectedIntegrationId(config.integrationId);
      }
      if (typeof config.projectId === 'string') {
        setProjectId(config.projectId);
      }
      if (typeof config.issueTypeId === 'string') {
        setIssueTypeId(config.issueTypeId);
      }
    }
  }, [existingSkill?.config]);

  // Show the saved issue type once the project's types have loaded, unless the
  // admin cleared it for this project.
  //
  // Both halves matter. Without the restore, returning to the saved project after
  // looking at another one leaves the field empty while the saved config still
  // holds a type, and saving from there removes the default without the admin
  // asking. Without the cleared check, clearing the field puts the value straight
  // back and the default cannot be removed at all.
  useEffect(() => {
    if (!projectId || issueOptions.length === 0) {
      return;
    }
    if (clearedByAdminForProject.current === projectId || issueTypeId) {
      return;
    }
    const savedIssueTypeId = existingSkill?.config?.issueTypeId;
    if (typeof savedIssueTypeId !== 'string') {
      return;
    }
    if (issueOptions.some((option) => option.value === savedIssueTypeId)) {
      setIssueTypeId(savedIssueTypeId);
    }
  }, [
    existingSkill?.config?.issueTypeId,
    projectId,
    issueOptions,
    issueTypeId,
  ]);

  const handleSubmit = async () => {
    if (isDisabled) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    // Only send a type the selected project actually offers. The reset effect
    // compares against the saved project rather than the previous selection, so
    // a type from another project can survive in state; sending it would save a
    // default the project cannot use.
    const belongsToProject = issueOptions.some(
      (option) => option.value === issueTypeId,
    );

    try {
      await enableSkill({
        skillType: skill.id,
        enabled: true,
        config: {
          integrationId: selectedIntegrationId,
          projectId: projectId,
          // Omitted rather than sent empty when no default is chosen. The
          // default issue type is optional, but an empty value is not valid.
          ...(issueTypeId && belongsToProject ? { issueTypeId } : {}),
        },
      });
      await onComplete();
    } catch (error) {
      setError(t('failedToConfigureJiraPleaseTryAgain'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasExistingConfig =
    typeof existingSkill?.config?.integrationId === 'string' ||
    typeof existingSkill?.config?.projectId === 'string' ||
    typeof existingSkill?.config?.issueTypeId === 'string';
  const isReconfiguring = Boolean(
    existingSkill?.enabled || skill.enabled || hasExistingConfig,
  );
  const hasSingleIntegration = jiraOptions.length === 1;
  const hasNoIntegrations = jiraOptions.length === 0;

  // Poll for integrations when there are none, so we auto-detect additions
  useEffect(() => {
    if (isLoadingIntegrations || !hasNoIntegrations) {
      return;
    }
    const intervalId = window.setInterval(() => {
      void refetchIntegrations();
    }, 4000);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [hasNoIntegrations, isLoadingIntegrations, refetchIntegrations]);

  const isDisabled =
    isLoading ||
    isEnabling ||
    isSubmitting ||
    isLoadingIntegrations ||
    hasNoIntegrations ||
    !selectedIntegrationId ||
    !projectId;

  const selectedOption = jiraOptions.find(
    (option) => option.value === selectedIntegrationId,
  );

  return (
    <div className="mx-auto max-w-2xl">
      {/* 1. Title */}
      <div className="mb-8 text-center">
        <h2 className="text-2xl leading-tight font-semibold text-gray-900 dark:text-gray-100">
          {t('jira')}
        </h2>
      </div>
      <div className="rounded-lg bg-white p-6 dark:bg-gray-900">
        {/* 2. Logo */}
        <div className="mb-8 text-center">
          <div className="relative mx-auto inline-block">
            <div className="absolute inset-0 animate-[pulse_4s_ease-in-out_infinite] rounded-xl bg-blue-50 blur-xl dark:bg-blue-950/10" />
            {skill.image ? (
              <Image
                src={skill.image.src}
                alt={skill.image.alt}
                className="relative h-20 rounded-xl object-cover"
              />
            ) : (
              <div className="relative flex h-20 w-20 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500/20 to-indigo-600/20">
                <span className="text-4xl leading-tight">{skill.icon}</span>
              </div>
            )}
          </div>
        </div>

        {/* 3. Short Description */}
        <div className="mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400">
            {t('iCanRaiseTrackAndUpdateJira')}
          </p>
        </div>

        {/* 4. Learn More Link */}
        <div className="mb-8 text-center">
          <p className="text-sm text-gray-500">
            <a
              href="https://docs.pleri.ai/tools/jira"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline hover:text-blue-700"
            >
              {t('clickHereToLearnMore')}
            </a>
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-8 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-950">
            <div className="flex items-center gap-2">
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* 5. Form Fields */}
        <div className="mb-8">
          <div className="mx-auto max-w-md">
            {isLoadingIntegrations ? (
              <div className="w-full">
                <div className="flex w-full items-center gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('loadingYourJira')}
                  </p>
                </div>
              </div>
            ) : hasNoIntegrations ? (
              <div className="space-y-4">
                <div className="w-full">
                  <div className="mb-4 rounded-lg bg-blue-50 p-4 dark:bg-blue-950">
                    <div className="flex items-start gap-3">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                        <svg
                          className="h-3 w-3 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          {t('addJiraIntegration')}
                        </p>
                        <p className="mt-1 text-sm text-blue-700">
                          <Trans
                            t={t}
                            i18nKey="visitAddJiraIntegrationPageToAdd"
                            components={{
                              a: (
                                <a
                                  href="/settings/integrations/add/Jira/Jira"
                                  className="underline hover:text-blue-800"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={t(
                                    'openAddJiraIntegrationPageInA',
                                  )}
                                />
                              ),
                            }}
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {hasSingleIntegration ? (
                  <div className="w-full">
                    <div className="flex w-full items-center gap-2 rounded-lg bg-blue-50 p-2 dark:bg-blue-950">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900">
                        <svg
                          className="h-3 w-3 text-blue-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-blue-800">
                        {selectedOption?.name || t('jiraIntegration')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('integration')}
                    </div>
                    <Select
                      key={`jira-integration-${selectedIntegrationId}`}
                      options={jiraOptions}
                      defaultSelectedIndex={
                        selectedIntegrationId
                          ? jiraOptions.findIndex(
                              (option) =>
                                option.value === selectedIntegrationId,
                            )
                          : -1
                      }
                      placeholder={t('chooseYourJira')}
                      onUpdate={handleIntegrationUpdate}
                      wrapperClassName="w-full !gap-0 text-sm"
                      hideCloseIcon={true}
                    />
                  </div>
                )}

                {selectedIntegrationId && (
                  <div className="mt-4">
                    <div className="mb-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('iWillReadCreateAndUpdateJira')}
                      </p>
                    </div>
                    {isLoadingJiraProjects ? (
                      <div className="flex w-full items-center gap-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 dark:border-gray-600" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {t('loadingProjects')}
                        </p>
                      </div>
                    ) : (
                      <div>
                        <div className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t('project')}
                        </div>
                        <Select
                          key={`jira-project-${projectId}`}
                          options={projectOptions}
                          defaultSelectedIndex={
                            projectId
                              ? projectOptions.findIndex(
                                  (option) => option.value === projectId,
                                )
                              : -1
                          }
                          placeholder={t('chooseJiraProject')}
                          onUpdate={handleProjectUpdate}
                          wrapperClassName="w-full !gap-0 text-sm"
                          hideCloseIcon={true}
                        />
                      </div>
                    )}
                  </div>
                )}

                {projectId && issueOptions.length > 0 && (
                  <div className="mt-4">
                    <div className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t('defaultIssueTypeOptional')}
                    </div>
                    <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">
                      {t('usedWhenARequestDoesNotSay')}
                    </p>
                    <Select
                      key={`jira-issue-type-${issueTypeId}`}
                      options={issueOptions}
                      defaultSelectedIndex={
                        issueTypeId
                          ? issueOptions.findIndex(
                              (option) => option.value === issueTypeId,
                            )
                          : -1
                      }
                      placeholder={t('noDefault')}
                      onUpdate={handleIssueTypeUpdate}
                      wrapperClassName="w-full !gap-0 text-sm"
                      // Clearable on purpose: every tenant configured before
                      // this had a type pinned, and they need a way back to
                      // having no default.
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* 6. Call to Action Button */}
        {!isLoadingIntegrations && !hasNoIntegrations && (
          <div className="mb-6 flex flex-col items-center">
            <Button
              onClick={handleSubmit}
              disabled={isDisabled}
              className={`group relative flex w-full items-center justify-center rounded-lg px-8 py-3 text-sm font-medium transition-all ${
                isDisabled
                  ? 'cursor-not-allowed bg-gray-300 text-gray-500 dark:bg-gray-600 dark:text-gray-400'
                  : ''
              }`}
              data-testid="jira-activate-button"
            >
              {isReconfiguring ? t('updateConfiguration') : t('activateJira')}
            </Button>
          </div>
        )}

        {/* 7. Cancel or Go Back Button */}
        {!isLoadingIntegrations && !hasNoIntegrations && (
          <div className="flex flex-col items-center">
            <button
              onClick={onCancel}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            >
              {t('common:cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
